<template>
    <div class="content-block dx-card responsive-paddings">
        <div>
            <div
                v-if="hasEditPermissions && isDefaultAdmin"
                class="app-buttons"
            >
                <dx-button
                    id="create"
                    icon="plus"
                    type="normal"
                    :text="$t('general.create')"
                    :hint="$t('locations.dictionaryItem.create.hint')"
                    @click="onCreate"
                />
            </div>

            <dx-tree-list
                v-if="items"
                class="data-grid"
                :data-source="items"
                :show-borders="true"
                :loadPanel="{ enabled: loading }"
                key-expr="location.id"
                parent-id-expr="location.parent.id"
                :show-row-lines="true"
                ref="dataGrid"
                :height="gridHeight"
                @row-prepared="onRowPrepared"
                @cell-prepared="onCellPrepared"
                @row-click="onRowClick"
                @context-menu-preparing="addMenuItems"
                @editor-preparing="onEditorPreparing"
                @initialized=initializedGrid
            >
                <dx-filter-row :visible="true" />
                <dx-header-filter :visible="true"/>
                <dx-scrolling mode="virtual" />
                <dx-filter-panel :visible="showFilterPanel" />
                <dx-paging :enabled="true" />
                <dx-sorting mode="multiple" />

                <dx-column
                    :width="120"
                    data-field="location.code"
                    :caption="$t('locations.dictionaryItem.code')"
                    :allow-header-filtering="false"
                    :allow-filtering="true"
                    :editor-options="{maxLength: 50}"
                />

                <dx-column
                    data-field="location.name"
                    :caption="$t('locations.dictionaryItem.name')"
                    :allow-header-filtering="false"
                    :allow-filtering="true"
                    :editor-options="{maxLength: 250}"
                />

                <dx-column
                    data-field="dateFrom"
                    :caption="$t('licenses.dateFrom')"
                    data-type="date"
                    format="dd.MM.yyyy"
                    :allow-header-filtering="false"
                    :allow-filtering="true"
                />

                <dx-column
                    data-field="dateTo"
                    :caption="$t('licenses.dateTo')"
                    data-type="date"
                    format="dd.MM.yyyy"
                    :allow-header-filtering="false"
                    :allow-filtering="true"
                />

                <dx-column
                    data-field="deviceCount"
                    :caption="$t('licenses.deviceCount')"
                    data-type="number"
                    :allow-header-filtering="false"
                    :allow-filtering="true"
                />

                <dx-column
                    data-field="activeDeviceCount"
                    :caption="$t('licenses.activeDeviceCount')"
                    data-type="number"
                    :allow-header-filtering="false"
                    :allow-filtering="true"
                />

                <dx-column
                    data-field="dbDeviceCount"
                    :caption="$t('licenses.dbDeviceCount')"
                    data-type="number"
                    :allow-header-filtering="false"
                    :allow-filtering="true"
                />
            </dx-tree-list>

            <CustomColumnChooser
                :visible="visible"
                :columns="columns"
                :onApply="onApply"
                :onHiding="onHiding"
            />
        </div>
    </div>
</template>

<script>
import baseView from "../base.vue";
import appInfo from "../../app-info";

import {
  DxTreeList, DxColumn, DxFilterRow, DxHeaderFilter, DxPaging, DxScrolling,
  DxSorting, DxFilterPanel
} from 'devextreme-vue/tree-list';
import { DxButton } from 'devextreme-vue';
import moment from 'moment';

import CustomColumnChooser from "@/components/column-chooser/CustomColumnChooser.vue";

import { AclHandler } from "@/services/acl-handler";
import { userLicensesRelevant } from "@/services/api";

import { DataBacked } from "@/mixins/dataBackedMixin";
import { DataDrid } from "@/mixins/dataDridMixin";

import {
  PAGE_HEADER_HEIGHT,
  PAGE_PADDING,
  PAGE_TOOLBARS_HEIGHT,
  PAGE_FOOTER_HEIGHT,
  GRID_HEADER_HEIGHT,
  GRID_FILTER_ROW_HEIGHT,
  GRID_FILTER_PANEL_HEIGHT,
} from '@/shared/consts/layout';

import { findIndex } from 'lodash';

export default {
    name: 'License',
    mixins: [ DataBacked, DataDrid ],
    extends: baseView,
    components: {
        DxTreeList, DxColumn, DxFilterRow, DxHeaderFilter, DxPaging, DxScrolling,
        DxSorting, DxButton, CustomColumnChooser, DxFilterPanel
    },
    data () {
        return {
            isCloudMode: this.$store.getters.isCloudMode,
            loading: false,
            items: [],
        }
    },
    computed: {
        appUser() {
            return this.$store.getters.user;
        },
        aclUser() {
            return new AclHandler(this.appUser, this.isCloudMode);
        },
        isDefaultAdmin() {
            return this.aclUser.hasPermission({ roleCodes : ['ADMIN', 'DEVELOPER'] }) && this.aclUser.hasDefaultLocation();
        },
        showFilterPanel() {
            return this.aclUser.hasGrantForOperation('BTN_CLEAR_FILTER', 'EXECUTE') || false;
        },
        hasEditPermissions() {
            return this.aclUser.hasGrantForOperation('BTN_LICENSE_UPDATE', 'EXECUTE') || false;
        },
        sumGap() {
            const padding = 40 * 2;
            return padding + PAGE_HEADER_HEIGHT + PAGE_PADDING + PAGE_TOOLBARS_HEIGHT + PAGE_FOOTER_HEIGHT;
        },
        gridHeight() {
            const appHeightSize = this.$store.getters.appContainerSize.height;
            return (appHeightSize || 800) - this.sumGap;
        },
        gridBodyHeight() {
            return this.gridHeight - GRID_HEADER_HEIGHT - GRID_FILTER_ROW_HEIGHT - (this.showFilterPanel ? GRID_FILTER_PANEL_HEIGHT : 0);
        },
    },
    methods: {
        onRowPrepared(e) {
          if (e.rowType === 'data') {
                // Закрашивание строки, в зависимости от даты окончания лицензии
                const today = moment()
                const monthFromToday = today.clone().add(1, 'month')
                const dateTo = moment(e.data.dateTo)

                if (today.isAfter(dateTo)) {
                    e.rowElement.style.color = 'rgb(255,0,0)';
                } else if (monthFromToday.isAfter(dateTo)) {
                    e.rowElement.style.color = 'rgb(255,165,0)';
                } else {
                    e.rowElement.style.color = 'rgb(0,128,0)';
                }
            }
        },
        onCellPrepared(e) {
            if (e.rowType === 'data' && e.column.dataField === 'activeDeviceCount') {
                // Закрашивание ячейки обслуживаемых устройств, в зависимости от их количества 
                const deviceCount = e.data.deviceCount;
                const activeDeviceCount = e.data.activeDeviceCount;
                const lessThanThreeExpired = deviceCount - activeDeviceCount <= 3;
                const lessThanThreePercentExpired = (activeDeviceCount / deviceCount) >= 0.97;

                if (lessThanThreeExpired || lessThanThreePercentExpired) {
                    e.cellElement.style.color = 'rgb(0,0,0)';
                    e.cellElement.style.backgroundColor = 'rgb(255,165,0)';
                }
            }
        },
        onEditorPreparing(e) {
            this.applyFilterRowTimeout(e);
        },
        onRowClick(e) {
            const target = e.event.target;
            const isIconExpandCollapse = target.closest('.dx-treelist-collapsed') || target.closest('.dx-treelist-expanded');
            if (window.getSelection().focusOffset !== window.getSelection().anchorOffset || isIconExpandCollapse || !e.data.location.id)
                return;

            if (e.event.metaKey || e.event.ctrlKey) {
                const routeData = this.$router.resolve({
                    name: "location",
                    params: { id: e.data.location.id }
                });
                window.open(routeData.href, "_blank");
            } else {
                this.$router.push({
                    name: "location",
                    params: { id: e.data.location.id }
                });
            }
        },
        onCreate() {
            this.$router.push({ name: "license", params: { id: -1 } });
        },
    },
    async created() {
        try {
            this.loading = true;
            const licensesRelevant = await userLicensesRelevant();

            // найти корневые локации если parent этой локации отсутствует в списке или parent = null
            licensesRelevant.forEach((el, index) => {
                if (!el.location.parent)
                    return;

                const locationIndex = findIndex(licensesRelevant, ['location.id', el.location.parent.id]);
                if (locationIndex === -1)
                    licensesRelevant[index].location.parent = null;
            });

            this.items = licensesRelevant;
        } catch (error) {
            this.items = [];
            this.onDataError(error);
        } finally {
            this.loading = false;
        }

        document.title = `${appInfo.title} - ${this.$t('licenses.title')}`;
        this.$store.commit("setBreadcrumb", [{
            link: "/licenses",
            title: this.$t('licenses.title')
        }]);
    }
}
</script>

<style lang="scss" scoped>

</style>